import type { GalleryImage, GalleryVideoItem } from './ImageGallery.common';

/**
 * Where the intro video sits in the gallery: second, behind the sitter's
 * profile photo.
 */
export const INTRO_VIDEO_SLOT_INDEX = 1;

/**
 * Places a sitter's intro video among their photos.
 *
 * This is the only place the ordering rule lives. Every surface that shows
 * intro videos calls it rather than splicing for itself, so the video cannot
 * drift to a different slot on one surface. Surfaces that are not part of the
 * feature simply do not call it — which is why the insertion happens here and
 * not inside `useGalleryImages`, whose consumers include pages the feature was
 * never designed for.
 *
 * Pure: the input array is never mutated. With no video the original array is
 * returned by reference, so a call site with nothing to insert cannot
 * invalidate a downstream memo.
 */
const insertIntroVideoItem = <T extends GalleryImage>(
  images: T[] | undefined,
  video: GalleryVideoItem | undefined,
  slotIndex: number = INTRO_VIDEO_SLOT_INDEX
): Array<T | GalleryVideoItem> | undefined => {
  if (!images || !video) return images;

  // A slot beyond the end appends rather than leaving a hole; a negative slot
  // leads the array. Callers pass counts that can legitimately be short — a
  // sitter with no photos still has a profile photo slot.
  const insertAt = Math.min(Math.max(slotIndex, 0), images.length);

  return [...images.slice(0, insertAt), video, ...images.slice(insertAt)];
};

export default insertIntroVideoItem;
